<script setup lang="ts">
import { watch, ref } from 'vue'
import { RouterView } from 'vue-router'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { useActorStore } from './stores/actor'
import { useQueryClient } from '@tanstack/vue-query'
import { useFeatureFlagsQuery } from './composables/api/features'
import { useAuthStore } from './stores/auth'
import ActorSwitchModal from './components/modals/ActorSwitchModal.vue'
import Navbar from './components/Navbar.vue'
import RouteTransition from './router/RouteTransition.vue'
import ConsentModal from '@/components/modals/ConsentModal.vue'
import * as Sentry from '@sentry/browser'
import { Auth0 } from './config/auth'
import { updateUser, shutdown, initializeIntercom } from 'vue-intercom'
import type { User } from '@auth0/auth0-vue'
import { watchDeep } from '@vueuse/core'

const authStore = useAuthStore()
const actorStore = useActorStore()
const showConsentForm = ref(false)
useFeatureFlagsQuery()
const queryClient = useQueryClient()

watchDeep(
  () => authStore.getTrackedFeatures,
  (newTrackedFeatures) => {
    if (newTrackedFeatures) {
      updateUser({
        'Verified Contact Info Feature': newTrackedFeatures.get_contact_info,
        'Personal Contact Info Feature': newTrackedFeatures.get_contact_info_personal
      })
    }
  }
)
watchDeep(
  () => actorStore.getCurrentTrackingData,
  (newTrackingData) => {
    if (newTrackingData) {
      initializeIntercom()
      // If we have tracking data, we can safely assume we also have up to date user data
      const newData = {
        intercom_user_jwt: newTrackingData.intercomUserJwt,
        email: Auth0.user.value?.email,
        name: Auth0.user.value?.name,
        user_id: Auth0.user.value?.sub,
        user_type: 'Admin Explore',
        actorId: newTrackingData.actorId,
        'Axuall Permissions': newTrackingData.role
      }
      updateUser(newData)
      authStore.trackUser()
    }
  }
)
watch(Auth0.user, (newUser: User | undefined) => {
  if (newUser !== undefined) {
    const userObj = { email: newUser.email, username: newUser.email, id: newUser.sub }
    const scope = Sentry.getCurrentScope()
    scope.setUser(userObj)
    showConsentForm.value = true
    actorStore.initialize()
    queryClient.refetchQueries({ queryKey: ['features'] })
  } else {
    queryClient.removeQueries({ type: 'all' })
    authStore.features = undefined
    Sentry.setUser(null)
    shutdown()
  }
})
</script>

<template>
  <div class="has-background-primary-dark-gradient full-app">
    <div class="hero-head">
      <navbar />
    </div>
    <router-view v-slot="{ Component, route }">
      <RouteTransition :route="route">
        <div :key="route.name">
          <component :is="Component" />
        </div>
      </RouteTransition>
    </router-view>
    <VueQueryDevtools button-position="bottom-left" />
    <ActorSwitchModal />
    <div v-if="showConsentForm">
      <consent-modal />
    </div>
  </div>
</template>

<style lang="scss">
@import './assets/scss/style.scss';
.full-app {
  min-height: 100vh;
}
</style>
